import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from './auth';

/**
 * A minimal fetch hook.
 *
 * Deliberately not TanStack Query. This app has a handful of screens and no
 * cross-screen cache invalidation to speak of yet; adding a cache layer now
 * would mean maintaining cache keys before there is a cache problem to solve.
 * The shape below is what a query library would hand back, so swapping one in
 * later is a change of import rather than a rewrite of every screen.
 */
export function useQuery<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): { data: T | null; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);

    fetcher()
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(errorMessage(e)); })
      .finally(() => { if (live) setLoading(false); });

    // Guards against a slow response from a previous filter landing after a
    // faster one from the current filter — which would show the wrong rows.
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload };
}
